import React, { useState, lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';
import sortBy from 'lodash/sortBy';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link as ScrollLink, Element } from 'react-scroll';
import _get from 'lodash/get';
import { Container } from 'reactstrap';
import { useLocation } from 'react-router-dom';

import AgeRatings from 'Components/Utility/Age/AgeRatings';
import BundleCarousel from 'Components/BundleCarousel/BundleCarousel';
import BundleCommerceDetails from 'Features/Bundle/components/Commerce/BundleCommerceDetails';
import BundleEnded from 'Features/Bundle/components/BundleEnded/BundleEnded';
import BundleKeySellingPoints from 'Features/Bundle/components/KeySellingPointBars/BundleKeySellingPoints';
import BundlePageRedesign from 'Features/Bundle/components/BundleRedesign/BundlePageRedesign';
import BundleProductBar from 'Components/BundleProductBar/BundleProductBar';
import BundleTimer from 'Components/Timers/BundleTimer';
import CharityLeaderboard from 'Features/Bundle/components/PayWhatYouWant/CharityLeaderboard';
import DangerousHTML from 'Components/Utility/DangerousHTML';
import DonationTotal from 'Features/Bundle/components/PayWhatYouWant/DonationTotal';
import EbookTrustBar from 'Components/Ebook/EbookTrustBar';
import ItemList from 'Components/Utility/SEO/ItemList';
import PaymentCards from 'Components/PaymentCards/PaymentCards';
import ProductBreadcrumbs from 'Components/Utility/SEO/ProductBreadcrumbs';
import ProductCover from 'Components/Utility/ProductCover';
import ProductOffers from 'Components/ProductOffers/ProductOffers';
import ProductRecommendations from 'Components/ProductRecommendations/ProductRecommendations';
import ProductRequirements from 'Helpers/Product/ProductRequirements';
import ProductSEOHelmet from 'Components/Utility/SEO/ProductSEOHelmet';
import ProductStructuredData from 'Components/Utility/SEO/ProductStructuredData';
import SkeletonPlaceholder from 'Components/Utility/SkeletonPlaceholder';
import StarDealLabel from 'Components/StarDeal/StarDealLabel';
import TemplateSelectorAudioBundle from 'Features/Bundle/components/Templates/AudioTemplates/AudioBundleTemplateSelector';
import TemplateSelectorBookBundle from 'Features/Bundle/components/Templates/BookTemplates/BookBundleTemplateSelector';
import TemplateSelectorComicBundle from 'Features/Bundle/components/Templates/ComicTemplates/ComicBundleTemplateSelector';
import TemplateSelectorGameBundle from 'Features/Bundle/components/Templates/GameTemplates/GameBundleTemplateSelector';
import TemplateSelectorSoftwareBundle from 'Features/Bundle/components/Templates/SoftwareTemplates/SoftwareBundleTemplateSelector';
import VideoImgLoader from 'Components/Utility/Video/VideoImgLoader';
import { findValidDelivery } from 'Redux/ducks/deliveries';
import useTheme from 'Hooks/useTheme';
import useViewport from 'Hooks/useViewport';

import 'Features/Bundle/components/Views/BundleView.scss';

const HowToGiftBanner = lazy(() => import(/* webpackChunkName: "HowToGiftBanner" */ 'Components/Gift/HowToGiftBanner'));

const messages = defineMessages({
  msg_box_one_title: {
    id: 'bundlePage.title',
    defaultMessage: 'Purchase Limit',
  },
  msg_box_one_desc: {
    id: 'bundlePage.desc',
    defaultMessage: 'You are only permitted to buy a single copy of an individual product per order',
  },
  rec_header: {
    id: 'bundlePage.recommendations',
    defaultMessage: 'Other products you may like',
  },
  rec_bundle_header: {
    id: 'bundlePage.bundle_recommendations',
    defaultMessage: 'Other bundles you may like',
  },
  bundle: {
    id: 'bundlePage.game_bundle',
    defaultMessage: 'Game Bundle',
  },
  book_bundle: {
    id: 'bundlePage.book_bundle',
    defaultMessage: 'eBook Bundle',
  },
  software_bundle: {
    id: 'bundlePage.software_bundle',
    defaultMessage: 'Software Bundle',
  },
  steam_bundle: {
    id: 'bundlePage.steam_game_bundle',
    defaultMessage: 'Steam Game Bundle',
  },
  steamgame: {
    id: 'bundlePage.steamgame',
    defaultMessage: 'Steam Game',
  },
  game: {
    id: 'bundlePage.steam',
    defaultMessage: 'Game',
  },
});

function BundleView({
  product,
  starDeal,
  preSelectedPnm,
}) {
  const { formatMessage } = useIntl();
  const { isAboveMd } = useViewport();
  const location = useLocation();
  const showGiftBanner = location?.state?.from?.includes('gift-guide') ?? false;
  const siteTheme = useTheme();
  const deliveries = useSelector(state => state?.deliveries?.all);


  const [cardSelectedGame, setCardSelectedGame] = useState(1);

  const switchCarouselGame = (gameNumber) => {
    setCardSelectedGame(gameNumber);
  };

  if (!product || !Object.keys(product).length) {
    return <SkeletonPlaceholder />;
  }

  if (product.type === 'bundle') {
    let totalGames = 0;

    product.bundles.forEach((tier) => {
      let tierGames = 0;
      tierGames += tier.games.length;
      totalGames += tierGames;
      /* eslint-disable no-param-reassign */
      tier.deliverTotal = totalGames;
    });
  }

  let voucherDelivery;
  if (product && deliveries) {
    voucherDelivery = findValidDelivery(product, deliveries);
  }

  const {
    name,
    bundles,
    desc,
    cover,
    age: ageRatings,
    drm: platforms,
    type,
    slug,
    franchises,
    sold_out: soldOut,
    video,
    downloadTotal,
    display_type: displayType,
  } = product;

  let totalGameCount = 0;
  let deliveredGameCount = 0;
  let allGames = [];
  let pageTotal = 0;
  let filesTotal = 0;
  const displayTypes = [];
  const allDownloads = [];
  let couponIncluded = false;

  const bundleTiers = cloneDeep(bundles);
  bundleTiers.forEach((tier) => {
    let deliveryOverride = false;
    if (tier.deliveredProducts && tier.deliveredProducts > 0) {
      deliveryOverride = true;
      deliveredGameCount += tier.deliveredProducts;
    }

    sortBy(tier.games, ['sort_order']).filter(game => game.display).forEach((game) => {
      totalGameCount += 1;
      game.totalGameIndex = totalGameCount; // eslint-disable-line no-param-reassign

      if (!deliveryOverride) {
        deliveredGameCount += 1;
      }

      allGames = allGames.concat(game);
      displayTypes.push(game.display_type);
      if (game.pages) {
        pageTotal += Number(game.pages);
      }

      if (game.files) {
        filesTotal += Number(game.files);
      }
      if (!game.files && !game.downloads) {
        filesTotal += 1;
      }
      if (!game.files && game.downloads) {
        filesTotal += game.downloads.length;
      }

      if (game.downloads) {
        game.downloads.forEach((download) => {
          allDownloads.push(download);
        });
      }

      if (game.drm && game.drm.voucher) couponIncluded = true;
    });
  });

  const downloadTypes = [];
  allDownloads.forEach((download) => {
    if (!download.is_sample) {
      download.files.forEach((file) => {
        if (includes(downloadTypes, file.format)) return;
        if (file.format === 'zip') return;
        downloadTypes.push(file.format);
      });
    }
  });

  const allEqual = arr => arr.every(v => v === arr[0]);

  let validDrm = [];
  if (product.drm) {
    validDrm = Object.keys(product.drm).filter(p => product.drm[p]);
  }

  let endDate;
  if (product.availability) {
    if (product.availability.valid_until) {
      endDate = product.availability.valid_until;
    }
  }

  if (product.is_srp_bundle && product.current_discount && product.current_discount.percent) {
    endDate = product.current_discount.until;
  }

  let displayAgeRatings = true;
  if (
    ageRatings &&
    (ageRatings.ACB === 0 || ageRatings.ACB >= 20) &&
    (ageRatings.ESRB === 0 || ageRatings.ESRB >= 20) &&
    (ageRatings.PEGI === 0 || ageRatings.PEGI >= 20) &&
    (ageRatings.USK === 0 || ageRatings.USK >= 20)
  ) {
    displayAgeRatings = false;
  }

  const nameTooSmall = (smallName, onSteam) => {
    if (onSteam) {
      return `${smallName} - ${formatMessage(messages.steamgame)}`;
    }

    return `${smallName} - ${formatMessage(messages.game)}`;
  };

  const description = desc === null ? '' : desc;

  const mixedBundle = !allEqual(displayTypes);

  const charityBundle = _get(product, 'charity.active');
  const charityMessage = _get(product, 'charity.message');

  const isStarDeal = ProductRequirements.isStarDeal(product);

  const excludedTemplates = [
    'standard_mystery',
    'tiered_mystery',
  ];

  const isTiered = product?.bundles?.length > 1;
  const isRedesignTemplate = !excludedTemplates.includes(product.template_type);
  const showBundleRedesign = isRedesignTemplate && isAboveMd && isTiered;

  return (
    <main key={slug} className={classNames('bundle-page', { 'background-bundle': product.header })}>
      <ProductBreadcrumbs product={product} />
      <ProductSEOHelmet product={product} />
      <ProductStructuredData product={product} />
      <ItemList noLinks title="Games in this Bundle" hits={product.bundles.reduce((acc, tier) => acc.concat(tier.games), [])} />

      {showBundleRedesign &&
        <BundlePageRedesign
          product={product}
          deliveries={deliveries}
          siteTheme={siteTheme}
          preSelectedPnm={preSelectedPnm}
          bundleTiers={bundleTiers}
          switchCarouselGame={switchCarouselGame}
          totalGameCount={totalGameCount}
          deliveredGameCount={deliveredGameCount}
          filesTotal={filesTotal}
          downloadTotal={downloadTotal}
          pageTotal={pageTotal}
          validDrm={validDrm}
          voucherDelivery={voucherDelivery}
          downloadTypes={downloadTypes}
          mixedBundle={mixedBundle}
          allGames={allGames}
          cardSelectedGame={cardSelectedGame}
          endDate={endDate}
          isStarDeal={isStarDeal}
          displayTypes={displayTypes}
          couponIncluded={couponIncluded}
        />
      }

      {!showBundleRedesign &&
        <React.Fragment>
          <BundleProductBar bundle={product} tiers={bundleTiers} />

          {showGiftBanner &&
            <Suspense>
              <Container className="mt-4">
                <HowToGiftBanner smallBanner giftBannerHeadingTag="div" />
              </Container>
            </Suspense>
          }
          <React.Fragment>
            <Container fluid className="bundle-header">
              <section className="container">
                {soldOut && type === 'bundle' && !window.location.pathname.includes('/preview/') && !charityBundle &&
                  <BundleEnded
                    franchises={franchises}
                    slug={slug}
                    displayType={displayType}
                    darkTheme={siteTheme === 'dark'}
                  />
                }

                <div className="name-timer-container mobile-bundle-detail-container">
                  <div className="name-container">
                    <h1 className="product-name">
                      {name.length <= 7
                        && !preSelectedPnm ? nameTooSmall(name, platforms.steam) : name}
                    </h1>
                  </div>
                  {endDate && !soldOut &&
                    <div className="d-none d-md-block ml-3">
                      <BundleTimer
                        value={Date.parse(endDate)}
                        type={type}
                        starDeal={starDeal}
                        isSRPBundle={!!product.is_srp_bundle}
                      />
                    </div>
                  }
                </div>

                <div className="header-content-container">
                  <div className="cover-container">
                    {isStarDeal &&
                      <StarDealLabel flipLabel />
                    }
                    {!isEmpty(video) ?
                      <VideoImgLoader
                        cover={cover}
                        video={video[0]}
                        location={'bundlePage'}
                      />
                      :
                      <ProductCover
                        lazyLoad
                        cover={cover}
                        alt={`Product cover for ${name}`}
                        displayType={displayType}
                        sizesOverride="(min-width: 1520px) 662px, (min-width: 1200px) 562px, (min-width: 991px) 472px, (min-width: 768px) 352px, 98vw"
                      />
                    }
                  </div>

                  {charityBundle && soldOut ?
                    <div className="details-container">
                      <DonationTotal soldOut={soldOut} charity={product.charity} />
                    </div>
                    :
                    <React.Fragment>
                      <BundleCommerceDetails
                        product={product}
                        displayTypes={displayTypes}
                        totalGameCount={totalGameCount}
                        deliveredGameCount={deliveredGameCount}
                        couponIncluded={couponIncluded}
                        starDeal={starDeal}
                        preSelectedPnm={preSelectedPnm}
                      />
                      <div className="d-md-none mt-4">
                        <PaymentCards />
                      </div>
                    </React.Fragment>
                  }
                </div>
              </section>
            </Container>

            {endDate && !soldOut &&
              <div className="d-block d-md-none">
                <Container>
                  <BundleTimer
                    mobileTimer
                    value={Date.parse(endDate)}
                    type={type}
                    isSRPBundle={!!product.is_srp_bundle}
                    starDeal={starDeal}
                  />
                </Container>
              </div>
            }

            <Container>
              {charityBundle && charityMessage &&
                <div className="charity-read-more">
                  {charityMessage}
                  &nbsp;
                  <ScrollLink
                    spy
                    smooth
                    offset={-100}
                    activeClass="active"
                    to="bundle-desc"
                    duration={500}
                    className="charity-read-more-button"
                  >
                    <FormattedMessage id="bundle-page.readmore" defaultMessage="Read more..." />
                  </ScrollLink>
                </div>
              }
            </Container>

            {product.template_type !== 'tiered_mystery' && slug !== 'stardock-software-bundle' &&
              <BundleKeySellingPoints
                product={product}
                totalGameCount={totalGameCount}
                totalComicCount={filesTotal || downloadTotal}
                totalPageCount={pageTotal}
                validDrm={validDrm}
                voucherDelivery={voucherDelivery}
                downloadTypes={downloadTypes}
                mixedBundle={mixedBundle}
                starDeal={starDeal}
                deliveredGameCount={deliveredGameCount}
              />
            }

            <Container>
              {product.template_type === 'book' &&
                <EbookTrustBar
                  mixedBundle={mixedBundle}
                  downloadTypes={downloadTypes}
                  slug={slug}
                  drm={validDrm}
                />
              }

              {validDrm[0] === 'idw' &&
                <div className="product-message-banner">
                  <div className="message-container">
                    <FormattedMessage
                      id="bundle-page.message.idw.message"
                      defaultMessage="To access this content, you need to download the free IDW app and create or log in to your account."
                    />
                    &nbsp;
                    <a href="https://support.fanatical.com/hc/en/articles/23324336331538" target="_blank" rel="noopener noreferrer">
                      <FormattedMessage
                        id="bundle-page.message.idw.link"
                        defaultMessage="Find out more."
                      />
                    </a>
                  </div>
                </div>
              }

              {product.template_type !== 'comic' && product.template_type !== 'book' && product.template_type !== 'software' && product.template_type !== 'audio' &&
                <TemplateSelectorGameBundle
                  product={product}
                  bundleTiers={bundleTiers}
                  switchCarouselGame={switchCarouselGame}
                />
              }

              {product.template_type === 'comic' &&
                <TemplateSelectorComicBundle
                  product={product}
                  bundleTiers={bundleTiers}
                  switchCarouselGame={switchCarouselGame}
                />
              }

              {product.template_type === 'book' &&
                <TemplateSelectorBookBundle
                  product={product}
                  bundleTiers={bundleTiers}
                  switchCarouselGame={switchCarouselGame}
                />
              }

              {product.template_type === 'audio' &&
                <TemplateSelectorAudioBundle
                  product={product}
                  bundleTiers={bundleTiers}
                  switchCarouselGame={switchCarouselGame}
                />
              }

              {product.template_type === 'software' &&
                <TemplateSelectorSoftwareBundle
                  product={product}
                  bundleTiers={bundleTiers}
                  switchCarouselGame={switchCarouselGame}
                />
              }

              {product.template_type !== 'standard_mystery' &&
                product.template_type !== 'tiered_mystery' &&
                <BundleCarousel
                  allGames={allGames}
                  totalGameCount={totalGameCount}
                  product={product}
                  selectedSlide={cardSelectedGame}
                />
              }

              {!preSelectedPnm &&
                <section className="mb-3">
                  <ProductOffers product={product} />
                </section>
              }

              <section className="description-age-container">
                <article className="product-description" >
                  {product.preorder_info &&
                    <h2>
                      <FormattedMessage
                        id="bundle-page.about.prepurchase"
                        defaultMessage="Pre-purchase Information"
                      />
                    </h2>
                  }

                  {product.preorder_info &&
                    <DangerousHTML
                      html={product.preorder_info === null ? '' : product.preorder_info}
                      className="preorder-bottom-border"
                    />
                  }

                  <Element name="bundle-desc" />

                  <h2>
                    <FormattedMessage id="bundle-page.about-this" defaultMessage="About this Bundle" tagName="span" />
                  </h2>

                  <DangerousHTML
                    html={description}
                  />
                </article>

                <aside className="description-right-column">
                  {displayAgeRatings &&
                    <div className="bundle-agerating">
                      <AgeRatings ratings={ageRatings} />
                    </div>
                  }
                  <Element name="charity-leaderboard">
                    <CharityLeaderboard charity={product.charity} />
                  </Element>
                </aside>
              </section>

              {!preSelectedPnm &&
                <div className="bundle-product-recommendations">
                  <ProductRecommendations
                    product={product}
                    title={formatMessage(messages.rec_header)}
                  />
                </div>
              }

            </Container>
          </React.Fragment>
        </React.Fragment>
      }
    </main>
  );
}

BundleView.propTypes = {
  product: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    cover: PropTypes.string,
    presale: PropTypes.bool,
    type: PropTypes.string,
    currentDiscount: PropTypes.shape({}),
    price: PropTypes.shape({}),
    fullPrice: PropTypes.shape({}),
    hideDiscount: PropTypes.bool,
    bundles: PropTypes.arrayOf(PropTypes.shape({})),
    endDate: PropTypes.string,
    productId: PropTypes.string,
    soldOut: PropTypes.bool,
    regions_included: PropTypes.array,
    regions_excluded: PropTypes.array,
    giveaway: PropTypes.bool,
    platforms: PropTypes.shape({
      windows: PropTypes.bool,
      mac: PropTypes.bool,
      linux: PropTypes.bool,
    }),
    drm: PropTypes.shape({}),
  }),
  starDeal: PropTypes.shape({}),
  preSelectedPnm: PropTypes.bool,
};

BundleView.defaultProps = {
  product: {
    _id: '',
    name: '',
    cover: 'default.jpg',
    presale: false,
    type: 'game',
    currentDiscount: {},
    price: {},
    fullPrice: {},
    hideDiscount: false,
    bundles: [],
    endDate: '',
    productId: '',
    soldOut: false,
    regions_included: [],
    regions_excluded: [],
    giveaway: false,
    platforms: {
      windows: false,
      mac: false,
      linux: false,
    },
    drm: {},
    no_release_date: false,
  },
  allSlugs: [],
  starDeal: {},
  preSelectedPnm: false,
};

const areEqual = (prevProps, nextProps) => {
  const prevProduct = prevProps.product;
  const nextProduct = nextProps.product;

  return prevProduct.slug === nextProduct.slug &&
    prevProduct._id === nextProduct._id;
};

export default React.memo(BundleView, areEqual);

